/** Exact footer marker; everything after this phrase is dropped in frontend rendering. */
const PROOFPOINT_ESSENTIALS_SCANNED_PHRASE =
  "This email has been scanned for spam and viruses by Proofpoint Essentials";

/**
 * Flexible-whitespace variant of {@link PROOFPOINT_ESSENTIALS_SCANNED_PHRASE}: NBSP / numeric entities / `&nbsp;`
 * between words — gateways sometimes inject HTML whitespace entities so plain `lastIndexOf` misses identical-looking
 * footers.
 */
const PROOFPOINT_ESSENTIALS_SCANNED_PHRASE_REGEX = (() => {
  const w = "(?:[\\s\\u00a0]|&nbsp;|&#(?:160|x0*[Aa]0);|&amp;(?:nbsp|#160|#x0*[Aa]0);)+";
  return new RegExp(
    `This${w}email${w}has${w}been${w}scanned${w}for${w}spam${w}and${w}viruses${w}by${w}Proofpoint${w}Essentials`,
    "gi"
  );
})();

function findLastProofpointEssentialsDisclaimerStart(body: string): number {
  let best = body.lastIndexOf(PROOFPOINT_ESSENTIALS_SCANNED_PHRASE);

  PROOFPOINT_ESSENTIALS_SCANNED_PHRASE_REGEX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PROOFPOINT_ESSENTIALS_SCANNED_PHRASE_REGEX.exec(body)) !== null) {
    best = Math.max(best, m.index);
  }

  return best;
}

function serializeTruncatedHtmlFragment(clipped: string): string {
  if (typeof document === "undefined") {
    return clipped.replace(/[\s\n\r\u00a0]+$/u, "");
  }
  const tpl = document.createElement("template");
  tpl.innerHTML = clipped;
  return tpl.innerHTML.trimEnd().replace(/[\s\n\r\u00a0]+$/u, "");
}

export function cleanMessageBody(body: string): string {
  if (!body || typeof body !== "string") {
    return "";
  }
  return body
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Normalize common plaintext convention "See terms<https://…>" before linkifying.
 * Does not apply to `click here <https://…>` / multi-line Proofpoint-style footers — those are handled in
 * `buildPlaintextEmailDisplayHtml` so the visible label stays short.
 */
export function preprocessAngleBracketHyperlinks(raw: string): string {
  return raw.replace(/(\S[^\n<]*)<(https?:\/\/[^>\s]+)>/g, (_, before: string, url: string) => {
    const trimmed = typeof before === "string" ? before.trimEnd() : "";
    return `${trimmed}\n${url}`;
  });
}

/**
 * Drops trailing Proofpoint Essentials scanner footer when it appears as the final block (typical gateway banner).
 * Uses the **last** match so quoted/forwards are less likely to truncate real body content.
 *
 * For HTML mail, truncation runs through a `<template>` parse so an open `<p>` before the disclaimer does not
 * leave broken markup in the sanitized output.
 */
export function stripProofpointEssentialsScannerFooter(body: string, htmlMail = false): string {
  if (!body || typeof body !== "string") {
    return "";
  }
  const idx = findLastProofpointEssentialsDisclaimerStart(body);
  if (idx < 0) {
    return body;
  }
  const clipped = body.slice(0, idx);
  if (htmlMail) {
    return serializeTruncatedHtmlFragment(clipped);
  }
  return clipped.replace(/[\s\n\r\u00a0]+$/u, "");
}
